import { useEffect, useRef, useState, MutableRefObject } from "react";

import { useInterval } from "../utils/utils";
import { getApolloVehicles, getApolloLatest, getApolloMap } from "../utils/requestApiApollo";
import { Frame, MapData, Pt } from "../utils/apolloRenderer";

/**
 * Apollo 모니터링 데이터 파이프라인 훅 (렌더 기술 독립).
 * - 폴링(getApolloLatest/Map/Vehicles)
 * - entity interpolation 버퍼(프레임 타임스탬프 시간축) + 텔레포트 리셋 + 재생 클럭
 * 2D 캔버스/3D(R3F) 어느 렌더러든 공유한다. computeEgo(now)로 매 렌더 프레임 보간 위치를 얻는다.
 */

export type Vehicle = { carId: number; carNumber: string; host: string; port: number; enabled: boolean; connected: boolean; mapHash?: number };

const POLL_MS = 150;          // 실측 위치 버퍼를 촘촘히 (사이드카 push ~200ms)
const RENDER_DELAY_MS = 300;  // 두 실측 위치 '사이'를 보간(entity interpolation) → 예측 없음 = 진동 없음
const TELEPORT_M = 25;        // 이보다 큰 점프 = 루프 재시작(텔레포트) → 사선 없이 재등장
const MAX_TRAVELED = 4000;

export interface ApolloFrameApi {
    vehicles: Vehicle[];
    carId: number | null;
    setCarId: (v: number) => void;
    connected: boolean;
    errorMsg: string | null;
    mapData: MapData | null;
    frameRef: MutableRefObject<{ frame: Frame; arrivalMs: number } | null>;
    mapRef: MutableRefObject<MapData | null>;
    traveledRef: MutableRefObject<Pt[]>;
    /** 현재(now, performance.now() ms) 렌더용 보간 ego. 데이터 없으면 null. */
    computeEgo: (nowMs: number) => { x: number; y: number; heading: number } | null;
    /** 최신 프레임(장애물/궤적/HUD용). */
    latest: Frame | null;
}

export function useApolloFrame(): ApolloFrameApi {
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [carId, setCarId] = useState<number | null>(null);
    const [connected, setConnected] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [latest, setLatest] = useState<Frame | null>(null);
    const [mapData, setMapData] = useState<MapData | null>(null);

    const frameRef = useRef<{ frame: Frame; arrivalMs: number } | null>(null);
    const mapRef = useRef<MapData | null>(null);
    const traveledRef = useRef<Pt[]>([]);
    const egoBufRef = useRef<{ ts: number; x: number; y: number; heading: number; seq: number }[]>([]);
    const playRef = useRef<{ ts: number; wall: number } | null>(null);

    // 차량 목록 로드
    useEffect(() => {
        (async () => {
            try {
                const data = await getApolloVehicles();
                if (data?.result === "ok") {
                    const vs: Vehicle[] = data.vehicles || [];
                    setVehicles(vs);
                    if (vs.length > 0 && carId === null) setCarId(vs[0].carId);
                }
            } catch (e) {
                setErrorMsg("차량 목록을 불러올 수 없습니다.");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 차량 변경 → 맵 1회 로드 + 상태 초기화
    useEffect(() => {
        if (carId === null) return;
        setLatest(null);
        traveledRef.current = [];
        frameRef.current = null;
        mapRef.current = null;
        setMapData(null);
        egoBufRef.current = [];
        playRef.current = null;
        setErrorMsg(null);
        (async () => {
            try {
                const data = await getApolloMap(carId);
                if (data?.result === "ok" && data.map) { mapRef.current = data.map as MapData; setMapData(data.map as MapData); }
            } catch (e) { /* 맵은 라우팅 후 채워질 수 있음 */ }
        })();
    }, [carId]);

    // 최신 프레임 폴링
    useInterval(() => {
        if (carId === null) return;
        (async () => {
            try {
                const data = await getApolloLatest(carId);
                if (data?.result === "ok" && data.latest) {
                    const f = data.latest as Frame;
                    setLatest(f);
                    frameRef.current = { frame: f, arrivalMs: performance.now() };
                    setConnected(true);
                    setErrorMsg(null);
                    if (f.ego) {
                        const eb = egoBufRef.current;
                        const prev = eb.length ? eb[eb.length - 1] : null;
                        // 큰 점프(루프 재시작 텔레포트) → 사선/글라이드 없이 시작점 재등장
                        if (prev && Math.hypot(f.ego.x - prev.x, f.ego.y - prev.y) > TELEPORT_M) {
                            traveledRef.current = [];
                            egoBufRef.current = [];
                            playRef.current = null;
                        }
                        const tv = traveledRef.current;
                        tv.push([f.ego.x, f.ego.y]);
                        if (tv.length > MAX_TRAVELED) tv.shift();
                        const eb2 = egoBufRef.current;
                        const lastb = eb2.length ? eb2[eb2.length - 1] : null;
                        if (!lastb || (lastb.seq !== f.sequenceNum && f.timestamp > lastb.ts)) {
                            eb2.push({ ts: f.timestamp, x: f.ego.x, y: f.ego.y, heading: f.ego.heading, seq: f.sequenceNum });
                            if (eb2.length > 30) eb2.shift();
                        }
                    }
                    if (mapRef.current === null) {
                        const m = await getApolloMap(carId);
                        if (m?.result === "ok" && m.map) { mapRef.current = m.map as MapData; setMapData(m.map as MapData); }
                    }
                } else {
                    setConnected(false);
                }
            } catch (e) {
                setConnected(false);
                setErrorMsg("데이터 수신 실패(연결 확인)");
            }
        })();
    }, carId !== null ? POLL_MS : null);

    const computeEgo = (nowMs: number): { x: number; y: number; heading: number } | null => {
        const eb = egoBufRef.current;
        if (eb.length >= 2) {
            const latestTs = eb[eb.length - 1].ts;
            const targetTs = latestTs - RENDER_DELAY_MS;
            let rts: number;
            const play = playRef.current;
            if (play === null) {
                rts = targetTs;
            } else {
                rts = play.ts + (nowMs - play.wall);   // 프레임시간축을 실시간 속도로 전진
                rts += (targetTs - rts) * 0.005;        // 드리프트 아주 완만 보정(속도 워블 없이)
            }
            rts = Math.max(eb[0].ts, Math.min(rts, latestTs));
            playRef.current = { ts: rts, wall: nowMs };
            let i = 0;
            while (i < eb.length - 1 && eb[i + 1].ts <= rts) i++;
            const a = eb[i], b = eb[Math.min(i + 1, eb.length - 1)];
            const f = b.ts > a.ts ? Math.max(0, Math.min(1, (rts - a.ts) / (b.ts - a.ts))) : 0;
            let dh = b.heading - a.heading;
            while (dh > Math.PI) dh -= 2 * Math.PI;
            while (dh < -Math.PI) dh += 2 * Math.PI;
            return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, heading: a.heading + dh * f };
        }
        const fr = frameRef.current;
        if (fr && fr.frame.ego) return { x: fr.frame.ego.x, y: fr.frame.ego.y, heading: fr.frame.ego.heading };
        return null;
    };

    return { vehicles, carId, setCarId, connected, errorMsg, mapData, frameRef, mapRef, traveledRef, computeEgo, latest };
}
